import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { forwardsApi, isDomainError } from "@/forwards2/forwardsApi";
import {
  DEFAULT_MANDATE_STATUS_FILTERS,
  type AttentionRow,
  type MandateStatusFilter,
  type TimelineResponse,
} from "@/forwards2/forwardsTypes";
import { timelineQuarters } from "@/forwards2/periodUtils";
import { computed, reactive, readonly, ref, watch } from "vue";

/**
 * The dashboard's mandates panel state: the calendar year on screen, the status, company and name
 * filters, the timeline itself, and the mandates' Needs attention rows.
 *
 * The state is the panel's own and lives as long as it does: `MandatesTimelinePanelComponent` calls
 * this in its setup, and the dashboard's URL is the dashboard's own. `seedCompanyIds` seeds the
 * company filter — the open company in a single-company view, nothing in the consolidated one — and
 * the user may clear or change it. Both lists load at once and again whenever their filters change.
 * The name filter narrows the loaded rows without a refetch; the company filter narrows the
 * attention rows as well. Returned as one reactive object, so a template reads `timeline.loading`.
 */
export function useMandatesTimeline(seedCompanyIds: readonly number[]) {
  const { globalErrorHandler } = useGlobalHandlers();
  // Next year by default: forward mandates sell production ahead, and this year's is mostly sold or past.
  const year = ref(new Date().getFullYear() + 1);
  // A copy: the multiselect owns the array it is handed and the default must stay pristine.
  const statuses = ref<MandateStatusFilter[]>([
    ...DEFAULT_MANDATE_STATUS_FILTERS,
  ]);
  const companyIds = ref<number[]>([...seedCompanyIds]);
  const timeline = ref<TimelineResponse | null>(null);
  const loading = ref(false);
  const error = ref<unknown>(null);
  const nameFilter = ref("");
  const attentionRows = ref<AttentionRow[]>([]);
  const attentionError = ref<unknown>(null);

  /** The four quarter labels on screen. Taken from the response when it has arrived, so the
   *  columns and the rows can never disagree; derived locally while it is still loading. */
  const windowQuarters = computed(
    () => timeline.value?.window ?? timelineQuarters(year.value)
  );

  const rows = computed(() => timeline.value?.rows ?? []);
  const restrictedMandates = computed(
    () => timeline.value?.restricted_mandates ?? []
  );
  const nameMatches = computed(() => {
    const needle = nameFilter.value.trim().toLocaleLowerCase();
    return (name: string) => name.toLocaleLowerCase().includes(needle);
  });
  const filteredRows = computed(() =>
    rows.value.filter((row) => nameMatches.value(row.name))
  );
  const filteredRestrictedMandates = computed(() =>
    restrictedMandates.value.filter((mandate) =>
      nameMatches.value(mandate.name)
    )
  );
  const band = computed(() => timeline.value?.band ?? []);
  const companies = computed(() => timeline.value?.companies ?? []);
  const isEmpty = computed(
    () =>
      !loading.value &&
      error.value === null &&
      timeline.value !== null &&
      rows.value.length === 0 &&
      restrictedMandates.value.length === 0
  );
  /** The account has never had a mandate, so there is nothing to filter: the panel introduces forwards instead. */
  const hasNoMandates = computed(
    () => !loading.value && timeline.value?.has_mandates === false
  );
  /** Mandates were loaded, but the name filter hides every one of them. */
  const noNameMatches = computed(
    () =>
      !isEmpty.value &&
      timeline.value !== null &&
      filteredRows.value.length === 0 &&
      filteredRestrictedMandates.value.length === 0
  );

  // What each list is fetched for: a computed key changes only with its value, so a filter set back
  // to what is on screen does not fetch it again.
  const timelineKey = computed(() =>
    JSON.stringify([year.value, statuses.value, companyIds.value])
  );
  const attentionKey = computed(() => companyIds.value.join(","));

  let loadGeneration = 0;
  let attentionGeneration = 0;

  /**
   * Fetches the timeline for the current year and filters. Never throws — read `error`. A domain
   * error is a sentence the panel renders; anything else, a bug or an outage, also goes to the
   * app-wide handler.
   *
   * Only the latest call writes the timeline and clears `loading`: stepping the year twice
   * quickly starts two requests, and the earlier one may answer last.
   */
  const load = async () => {
    const generation = ++loadGeneration;
    loading.value = true;
    try {
      const response = await forwardsApi.getMandateTimeline({
        year: year.value,
        status: statuses.value,
        company_ids: companyIds.value,
      });
      if (generation !== loadGeneration) return;
      timeline.value = response.data;
      error.value = null;
    } catch (caught) {
      if (generation !== loadGeneration) return;
      error.value = caught;
      timeline.value = null;
      if (!isDomainError(caught)) globalErrorHandler(caught);
    } finally {
      if (generation === loadGeneration) loading.value = false;
    }
  };

  /**
   * The Needs attention rows of the mandates the company filter selects; the year and the status
   * filter do not narrow them. Never throws — read `attentionError`. Only the latest call writes,
   * as in `load`: a retry can overlap the mount's request, and the earlier one may answer last.
   */
  const loadAttention = async () => {
    const generation = ++attentionGeneration;
    try {
      const rows = (await forwardsApi.getMandatesAttention(companyIds.value))
        .data.rows;
      if (generation !== attentionGeneration) return;
      attentionRows.value = rows;
      attentionError.value = null;
    } catch (caught) {
      if (generation !== attentionGeneration) return;
      attentionError.value = caught;
    }
  };

  /** Fetches both lists again for the filters on screen, after a mandate was written elsewhere. */
  const reload = () => {
    void load();
    void loadAttention();
  };

  watch(timelineKey, () => void load(), { immediate: true });
  watch(attentionKey, () => void loadAttention(), { immediate: true });

  return reactive({
    year,
    statuses,
    companyIds,
    nameFilter,
    windowQuarters,
    rows,
    restrictedMandates,
    filteredRows,
    filteredRestrictedMandates,
    band,
    companies,
    isEmpty,
    hasNoMandates,
    noNameMatches,
    timeline: readonly(timeline),
    loading: readonly(loading),
    // A computed rather than `readonly()`: the value is a foreign error object and a readonly
    // proxy of it would break identity checks and `instanceof` for no benefit.
    error: computed(() => error.value),
    attentionRows: computed(() => attentionRows.value),
    attentionError: computed(() => attentionError.value),
    load,
    loadAttention,
    reload,
  });
}
